<template>
  <div class="layout">
    <!-- Skip to content link for keyboard accessibility -->
    <a href="#main-content" class="skip-to-content">Skip to main content</a>
    
    <!-- Global loading animation for page transitions -->
    <Transition name="fade">
      <div v-if="isNavigating" class="global-loading-overlay">
        <LoadingSpinner message="Loading page..." />
      </div>
    </Transition>

    <!-- Header -->
    <header class="header" :class="{ 'scrolled': isScrolled }">
      <div class="container header-inner">
        <div class="logo-container">
          <a href="/" class="logo" @click.prevent="navigateToHome">
            <img
              :src="mounted ? (colorMode.value === 'dark' ? '/logo-dark-mode.png' : '/logo.png') : '/logo-dark-mode.png'"
              alt="Tuck" class="logo-image" />
          </a>
        </div>
        <button 
          class="hamburger"
          :class="{ 'active': mobileMenuOpen, 'guest-budget-message': (route.path === '/wizard' && !user && !mobileMenuOpen) }"
          @click="toggleMobileMenu" 
          :aria-label="mobileMenuOpen ? 'Close menu' : 'Open menu'"
          :aria-expanded="mobileMenuOpen"
          aria-controls="mobile-navigation"
          type="button"
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </button> <!-- Navigation links container with mobile toggle -->
        <nav 
          class="nav-container" 
          :class="{ 'nav-open': mobileMenuOpen }"
          id="mobile-navigation"
          role="navigation"
          :aria-label="'Main navigation'"
        >
          <!-- Mobile-only user section at top -->
          <div class="nav-top mobile-only">
            <UserDropdown @click="closeMobileMenu" />
          </div>

          <!-- Scrollable navigation content -->
          <div class="nav-content">
            <nav class="nav">
              <a href="/" class="nav-link" @click.prevent="navigateToHome" v-if="user">Dashboard</a>
              <a href="/" class="nav-link" @click.prevent="navigateToHome" v-else>Home</a>
              <!-- Show these links only for authenticated users --> <template v-if="session?.user">
                <NuxtLink to="/budgets" class="nav-link" @click="closeMobileMenu" :active-class="''"
                  :exact-active-class="''">Budgets</NuxtLink>

                <!-- Admin Dropdown - Only visible to admin users -->
                <div v-if="isAdmin" class="dropdown">
                  <button class="nav-link dropdown-toggle" @click="toggleAdminDropdown">
                    Admin <span class="dropdown-caret" :class="{ 'rotated': adminDropdownOpen }">▼</span>
                  </button>
                  <div class="dropdown-menu" :class="{ 'visible': adminDropdownOpen }">
                    <NuxtLink to="/admin/users" class="dropdown-item" @click="closeMobileMenu" :active-class="''"
                      :exact-active-class="''">
                      <span class="dropdown-icon">👥</span> Manage Users
                    </NuxtLink>
                    <NuxtLink to="/admin/guides" class="dropdown-item" @click="closeMobileMenu" :active-class="''"
                      :exact-active-class="''">
                      <span class="dropdown-icon">📹</span> Manage Guides
                    </NuxtLink>
                    <NuxtLink to="/admin/feedback" class="dropdown-item" @click="closeMobileMenu" :active-class="''"
                      :exact-active-class="''">
                      <span class="dropdown-icon">💬</span> Manage Feedback
                    </NuxtLink>
                    <NuxtLink to="/admin/analytics" class="dropdown-item" @click="closeMobileMenu" :active-class="''"
                      :exact-active-class="''">
                      <span class="dropdown-icon">📊</span> Site Analytics
                    </NuxtLink>
                    <NuxtLink to="/admin/email-campaigns" class="dropdown-item" @click="closeMobileMenu"
                      :active-class="''" :exact-active-class="''">
                      <span class="dropdown-icon">📧</span> Email Campaigns
                    </NuxtLink>
                    <!-- Add more admin links here as they are created -->
                  </div>
                </div>
              </template><!-- Always show these links -->
              <div class="dropdown" v-if="!user">
                <button class="nav-link dropdown-toggle" @click="toggleDropdown">About <span class="dropdown-caret"
                    :class="{ 'rotated': dropdownOpen }">▼</span></button>
                <div class="dropdown-menu" :class="{ 'visible': dropdownOpen }">
                  <NuxtLink to="/about" class="dropdown-item" @click="closeMobileMenu" :active-class="''"
                    :exact-active-class="''">
                    About Us</NuxtLink>
                  <NuxtLink to="/privacy-policy" class="dropdown-item" @click="closeMobileMenu" :active-class="''"
                    :exact-active-class="''">Privacy Policy</NuxtLink>
                  <NuxtLink to="/terms-of-service" class="dropdown-item" @click="closeMobileMenu" :active-class="''"
                    :exact-active-class="''">Terms of Service
                  </NuxtLink>
                </div>
              </div>
              <NuxtLink to="/guides" class="nav-link" @click="closeMobileMenu" :active-class="''"
                :exact-active-class="''">Guides
              </NuxtLink>
              <NuxtLink to="/blog" class="nav-link" @click="closeMobileMenu" v-if="!user" :active-class="''"
                :exact-active-class="''">
                Blog</NuxtLink>
              <NuxtLink to="/choose-plan" class="nav-link" @click="closeMobileMenu" v-if="user && userPlan !== 'basic'" :active-class="''"
                :exact-active-class="''">Pricing</NuxtLink>
              <NuxtLink to="/pricing" class="nav-link" @click="closeMobileMenu" v-else-if="!user" :active-class="''"
                :exact-active-class="''">
                Pricing</NuxtLink>
              <NuxtLink to="/contact" class="nav-link" @click="closeMobileMenu" v-if="!user" :active-class="''"
                :exact-active-class="''">Contact</NuxtLink>
              <NuxtLink to="/settings" class="nav-link" @click="closeMobileMenu" v-if="user" :active-class="''"
                :exact-active-class="''">Settings</NuxtLink>

              <!-- Dark Mode Toggle -->
              <DarkModeToggle />
            </nav>
          </div>

          <!-- Desktop-only user section -->
          <div class="nav-right desktop-only">
            <UserDropdown @click="closeMobileMenu" />
          </div>
        </nav>

        <!-- Overlay for mobile menu -->
        <div v-if="mobileMenuOpen" class="menu-overlay" @click="closeMobileMenu"></div>
      </div>
    </header>

    <!-- Main content -->
    <main class="main" id="main-content" role="main">
      <slot />
    </main>

    <!-- Feedback Button (only show for authenticated users) -->
    <FeedbackButton v-if="session?.user" />
    
    <!-- Toast Notifications -->
    <ToastNotification />
    
    <!-- Footer -->
    <footer class="footer">
      <div class="container footer-inner">
        <div class="footer-content">
          <div class="footer-main">
            <p>&copy; {{ new Date().getFullYear() }} Tuck. All rights reserved.</p>
            <p class="footer-tip">"A budget is telling your money where to go instead of wondering where it went." -
              Dave
              Ramsey</p>
          </div>

          <div class="footer-legal">
            <div class="footer-links">
              <NuxtLink to="/about" class="footer-link" :active-class="''" :exact-active-class="''">About Us</NuxtLink>
              <NuxtLink to="/blog" class="footer-link" :active-class="''" :exact-active-class="''">Blog</NuxtLink>
              <NuxtLink to="/privacy-policy" class="footer-link" :active-class="''" :exact-active-class="''">Privacy
                Policy
              </NuxtLink>
              <NuxtLink to="/terms-of-service" class="footer-link" :active-class="''" :exact-active-class="''">Terms of
                Service</NuxtLink>
              <NuxtLink to="/contact" class="footer-link" :active-class="''" :exact-active-class="''">Contact</NuxtLink>
            </div>

            <div class="legal-notice">
              <p class="disclaimer-text">
                <strong>Important:</strong> This is not financial advice. Tuck is a budgeting tool for educational
                purposes
                only.
                We are not authorised or regulated by the Financial Conduct Authority (FCA).
                Seek independent financial advice from an FCA-authorised advisor before making financial decisions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import UserDropdown from '~/components/UserDropdown.vue';
import LoadingSpinner from '~/components/LoadingSpinner.vue';
import FeedbackButton from '~/components/feedback/FeedbackButton.vue';
import DarkModeToggle from '~/components/DarkModeToggle.vue';
import ToastNotification from '~/components/ToastNotification.vue';
import { useAuth, useRouter } from '#imports';
import { ref, onMounted, onUnmounted, watch, provide, computed } from 'vue';

const colorMode = await useColorMode();

// Track when component is mounted to prevent hydration mismatches
const mounted = ref(false);

// Get user session
const { session, user } = useAuth();
const router = useRouter();
const route = useRoute();

// Check if user is an admin
const isAdmin = computed(() => {
  const user = session.value?.user;
  return user && (user.role === 'admin' || user.email === 'admin@example.com');
});

// User plan information
const userPlan = ref(null);
const planSelectedAt = ref(null);

// Get the user's selected plan
onMounted(async () => {
  // Set mounted flag to prevent hydration mismatches
  mounted.value = true;

  // Check if plan is stored in localStorage
  if (process.client) {
    userPlan.value = localStorage.getItem('selectedPlan') || 'free';
    planSelectedAt.value = localStorage.getItem('planSelectedAt') || null;
    
    // If user is authenticated, fetch plan from database
    if (session.value?.user?.id) {
      try {
        const response = await fetch(`/api/user/plan?userId=${session.value.user.id}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.planInfo) {
            userPlan.value = data.planInfo.plan;
            planSelectedAt.value = data.planInfo.planSelectedAt;
            // Update localStorage to stay in sync
            localStorage.setItem('selectedPlan', data.planInfo.plan);
            if (data.planInfo.planSelectedAt) {
              localStorage.setItem('planSelectedAt', data.planInfo.planSelectedAt);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user plan:', error);
      }
    }
  }
});

// Helper function to check user's subscription plan
const getUserPlan = () => {
  if (process.client) {
    return {
      plan: userPlan.value || 'free',
      selectedAt: planSelectedAt.value,
      isNewUser: localStorage.getItem('isExistingUser') !== 'true'
    };
  }
  return { plan: 'free', selectedAt: null, isNewUser: false };
};

// Make user plan accessible to all components
provide('userPlan', getUserPlan);

// Monitor when user logs in to check if we need to save plan information to the database
watch(() => session.value?.user, async (newUser, oldUser) => {
  if (newUser && !oldUser && process.client) {
    const selectedPlan = localStorage.getItem('selectedPlan') || 'free';
    const planSelectedAt = localStorage.getItem('planSelectedAt');

    // Try to update the user's plan in the database
    try {
      const response = await fetch('/api/user/plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: newUser.id,
          plan: selectedPlan,
          selectedAt: planSelectedAt
        })
      });

      // Once plan is saved to the database, mark the user as existing
      if (response.ok) {
        localStorage.setItem('isExistingUser', 'true');
      }
    } catch (error) {
      console.error('Error saving plan information:', error);
    }
    
    // Fetch the latest plan from database after login
    try {
      const response = await fetch(`/api/user/plan?userId=${newUser.id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.planInfo) {
          userPlan.value = data.planInfo.plan;
          planSelectedAt.value = data.planInfo.planSelectedAt;
          // Update localStorage to stay in sync
          localStorage.setItem('selectedPlan', data.planInfo.plan);
          if (data.planInfo.planSelectedAt) {
            localStorage.setItem('planSelectedAt', data.planInfo.planSelectedAt);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user plan after login:', error);
    }
  }
});
const mobileMenuOpen = ref(false);
const isScrolled = ref(false);
const isNavigating = ref(false);
const dropdownOpen = ref(false);
const adminDropdownOpen = ref(false);


// Start the loading animation when navigation begins
router.beforeEach((to, from, next) => {
  if (to.path !== from.path) {
    isNavigating.value = true;
  }
  next();
});

// End the loading animation when navigation completes
router.afterEach(() => {
  isNavigating.value = false;
});

function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
  if (mobileMenuOpen.value) {
    // Prevent body scrolling when menu is open
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
}

function navigateToHome() {
  closeMobileMenu();
  router.push('/');
}

function toggleDropdown() {
  dropdownOpen.value = !dropdownOpen.value;
}

function toggleAdminDropdown() {
  adminDropdownOpen.value = !adminDropdownOpen.value;
}

function closeMobileMenu() {
  if (mobileMenuOpen.value) {
    mobileMenuOpen.value = false;
    document.body.style.overflow = '';
  }
  // Also close any open dropdowns
  dropdownOpen.value = false;
  adminDropdownOpen.value = false;
}

// Handle scroll event to change header appearance
function handleScroll() {
  isScrolled.value = window.scrollY > 30;
}

// Add and remove scroll event listener
onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
/* Import header styles from our CSS file */
@import url('../assets/css/header-navigation.css');

.layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--color-background);
  color: var(--color-text);
  font-family: 'Roboto', sans-serif;
}

/* Global loading overlay */
.global-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--color-background);
  opacity: 0.95;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  backdrop-filter: blur(3px);
}

/* Fade transition for loading overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}


/* Transition for the header when scrolling */
.header.scrolled {
  padding: 0.5rem 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.header.scrolled .logo-text {
  font-size: 1.6rem;
}

.header.scrolled .tagline {
  font-size: 0.8rem;
}

.main {
  flex-grow: 1;
  padding: 2rem 0;
}

/* Logo styles */
.logo-container {
  display: flex;
  flex-direction: column;
}

.logo {
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.logo-image {
  height: 3.5rem;
  width: auto;
  object-fit: contain;
}

.logo-text {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-primary);
  letter-spacing: -0.5px;
  position: relative;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.tagline {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin-top: 0.25rem;
  font-weight: 400;
}

/* Nav container and mobile menu toggle */
.nav-container {
  display: flex;
  align-items: center;
}

/* Desktop-only and mobile-only visibility */
.mobile-only {
  display: none;
}

.desktop-only {
  display: flex;
}

/* Desktop nav-right positioning */
.nav-right {
  margin-left: auto;
}

/* Navigation styles */
.nav {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.nav-link {
  color: var(--color-text);
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  padding: 0.5rem 0;
  position: relative;
  transition: color 0.3s ease;
}



.nav-link::after {
  content: '';
  position: absolute;
  width: 0;
  height: 2px;
  bottom: 0;
  left: 0;
  background-color: var(--color-primary);
  transition: width 0.3s ease;
}

/* Menu overlay for mobile */
.menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.hamburger {
  display: none;
  z-index: 10000;
}

.hamburger span {
  display: block;
  width: 25px;
  height: 2px;
  margin: 6px 0;
  background-color: var(--color-text);
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

/* Mobile responsive adjustments */
@media (max-width: 900px) {
  .guest-budget-message::after {
    content: 'Sign in without creating a budget';
    color: var(--color-white);
    opacity: 100%;
    transform: translateY(-20px) translateX(200px);
    padding: 10px;
    border-radius: 25px;
    background-color: var(--color-primary-light);
    z-index: 999;
    background-image: none;
    width: 265px;
    position: absolute;
    animation: bounce 2s ease-in-out infinite;
    text-align: center;
  }

  .guest-budget-message::before {
    content: '';
    position: absolute;
    transform: translateY(0px) translateX(15px);
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-left: 10px solid var(--color-primary-light);
    z-index: 999;
    animation: bounce-arrow 2s ease-in-out infinite;
  }

  @keyframes bounce {

    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(-37px) translateX(-300px);
    }

    40% {
      transform: translateY(-37px) translateX(-305px);
    }

    60% {
      transform: translateY(-37px) translateX(-300px);
    }
  }

  @keyframes bounce-arrow {

    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(7px) translateX(-37px);
    }

    40% {
      transform: translateY(7px) translateX(-42px);
    }

    60% {
      transform: translateY(7px) translateX(-37px);
    }
  }

  .guest-budget-message {
    overflow: visible;
  }

  .hamburger {
    display: block;
    z-index: 10000;
    position: relative;
  }

  header.scrolled .hamburger.active span {
    background-color: var(--color-text);
  }

  .hamburger.active span:nth-child(1) {
    transform: rotate(45deg) translate(5px, 6px);
  }

  .hamburger.active span:nth-child(2) {
    opacity: 0;
  }

  .hamburger.active span:nth-child(3) {
    transform: rotate(-45deg) translate(5px, -6px);
  }

  .header-inner {
    flex-wrap: wrap;
  }

  .mobile-menu-toggle {
    display: flex;
  }

  .nav-container {
    position: fixed;
    top: 0;
    right: -100%;
    width: 80%;
    max-width: 300px;
    height: 100vh;
    background-color: var(--color-white);
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
    transition: right 0.3s ease;
    z-index: 1000;
    display: flex;
    flex-direction: column;
  }

  .nav-container.nav-open {
    right: 0;
  }

  /* Show mobile-only, hide desktop-only */
  .mobile-only {
    display: block;
  }

  .desktop-only {
    display: none;
  }

  /* Mobile top section for user dropdown */
  .nav-top {
    padding: 1.5rem 2rem 1rem;
    border-bottom: 1px solid var(--color-border-light);
    background-color: var(--color-surface);
  }

  /* Scrollable content area */
  .nav-content {
    flex: 1;
    overflow-y: auto;
    padding: 1rem 2rem 2rem;
  }

  .nav {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }

  .nav-link {
    width: 100%;
    padding: 0.75rem 10px;
    border-bottom: 1px solid var(--color-border-light);
    font-size: 1.1rem;
  }

  .nav-link::after {
    display: none;
  }
}

@media (max-width: 768px) {
  .hamburger {
    display: block;
    z-index: 10000;
    position: relative;
  }

  header.scrolled .hamburger.active span {
    background-color: var(--color-text);
  }

  header.scrolled .hamburger.active {
    position: fixed;
    right: 20px;
    top: 20px;
  }

  .hamburger.active span:nth-child(1) {
    transform: rotate(45deg) translate(5px, 6px);
  }

  .hamburger.active span:nth-child(2) {
    opacity: 0;
  }

  .hamburger.active span:nth-child(3) {
    transform: rotate(-45deg) translate(5px, -6px);
  }

  .header-inner {
    flex-wrap: wrap;
  }

  .mobile-menu-toggle {
    display: flex;
  }

  .nav-container {
    position: fixed;
    top: 0;
    right: -100%;
    width: 80%;
    max-width: 300px;
    height: 100vh;
    background-color: var(--color-white);
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
    transition: right 0.3s ease;
    padding: 0;
    flex-direction: column;
    z-index: 1000;
  }

  .nav-container.nav-open {
    right: 0;
  }

  /* Show mobile-only, hide desktop-only */
  .mobile-only {
    display: block;
  }

  .desktop-only {
    display: none;
  }

  /* Mobile top section for user dropdown */
  .nav-top {
    padding: 1.5rem 2rem 1rem;
    border-bottom: 1px solid var(--color-border-light);
    background-color: var(--color-surface);
  }

  /* Scrollable content area */
  .nav-content {
    flex: 1;
    overflow-y: auto;
    padding: 1rem 2rem 2rem;
  }

  .nav {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }

  .nav-link {
    width: 100%;
    padding: 0.75rem 10px;
    border-bottom: 1px solid var(--color-border-light);
    font-size: 1.1rem;
  }

  .nav-link::after {
    display: none;
  }
}

.main {
  flex-grow: 1;
  padding: 2rem 0;
}

.footer {
  background-color: var(--color-surface);
  border-top: 1px solid var(--color-border-light);
  text-align: center;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  padding: 1.5rem 0;
  margin-top: auto;
}

.footer-inner {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.footer-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.footer-main {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.footer-legal {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-border-light);
}

.footer-tip {
  margin: 0;
  font-style: italic;
  color: var(--color-primary);
  font-weight: 500;
}

.footer-links {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.legal-notice {
  max-width: 800px;
  margin: 0 auto;
}

.disclaimer-text {
  font-size: 0.8rem;
  line-height: 1.4;
  color: var(--color-text-secondary);
  margin: 0;
  padding: 0.5rem;
  background: var(--color-warning-bg);
  border: 1px solid var(--color-warning);
  border-radius: 6px;
}

.footer-link {
  color: var(--color-primary);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.footer-link:hover {
  color: var(--color-primary-darker);
  text-decoration: underline;
}

/* Dropdown menu styles */
.dropdown {
  position: relative;
  display: inline-block;
}

.dropdown-toggle {
  background: var(--color-primary-bg);
  border: 1px solid var(--color-primary);
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0.4rem 0.8rem;
  font-size: inherit;
  color: var(--color-text);
  font-weight: 500;
  transition: all 0.2s ease-in-out;
}

.dropdown-toggle:hover {
  background: var(--color-primary-bg-hover);
  color: var(--color-primary);
}

.dropdown-caret {
  font-size: 0.7rem;
  margin-left: 0.5rem;
  transition: transform 0.3s ease;
}

.dropdown-caret.rotated {
  transform: rotate(180deg);
}

.dropdown-icon {
  margin-right: 0.5rem;
  display: inline-block;
}

.dropdown:hover .dropdown-caret {
  transform: rotate(180deg);
}

/* Intermediate screen size adjustments for dropdowns */
@media (max-width: 1080px) and (min-width: 901px) {
  .dropdown-toggle {
    padding: 0.35rem 0.6rem;
    font-size: 0.9rem;
  }

  .dropdown-caret {
    font-size: 0.6rem;
    margin-left: 0.4rem;
  }
}

@media (max-width: 1024px) and (min-width: 901px) {
  .dropdown-toggle {
    padding: 0.3rem 0.5rem;
    font-size: 0.85rem;
  }

  .dropdown-caret {
    font-size: 0.6rem;
    margin-left: 0.35rem;
  }
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  background-color: var(--color-white);
  min-width: 200px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  border-radius: 6px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.25s ease-in-out;
  border: 1px solid var(--color-primary-bg);
  overflow: hidden;
}

.dropdown:hover .dropdown-menu,
.dropdown-menu.visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  color: var(--color-text);
  padding: 0.75rem 1rem;
  text-decoration: none;
  display: flex;
  align-items: center;
  transition: all 0.2s;
  font-weight: normal;
  border-bottom: 1px solid var(--color-primary-bg);
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: var(--color-primary-bg);
  color: var(--color-primary);
  padding-left: 1.2rem;
}

@media screen and (max-width: 900px) {
  .dropdown-menu {
    position: static;
    box-shadow: none;
    opacity: 1;
    visibility: visible;
    transform: none;
    display: none;
    background-color: var(--color-primary-bg);
    border: none;
    border-radius: 4px;
    margin: 0.5rem 0 0.5rem 1rem;
    overflow: hidden;
  }

  .dropdown-menu.visible {
    display: block;
  }

  .dropdown:hover .dropdown-menu {
    display: block;
  }

  .dropdown-toggle {
    width: 100%;
    justify-content: space-between;
    background: none;
    border: none;
    padding: 0.75rem 10px;
    border-bottom: 1px solid var(--color-border-light);
  }

  .dropdown-item {
    border-left: 2px solid var(--color-primary);
    background-color: transparent;
  }
}

@media screen and (max-width: 768px) {
  .dropdown-menu {
    position: static;
    box-shadow: none;
    opacity: 1;
    visibility: visible;
    transform: none;
    display: none;
    background-color: var(--color-primary-bg);
    border: none;
    border-radius: 4px;
    margin: 0.5rem 0 0.5rem 1rem;
    overflow: hidden;
  }

  .dropdown-menu.visible {
    display: block;
  }

  .dropdown:hover .dropdown-menu {
    display: block;
  }

  .dropdown-toggle {
    width: 100%;
    justify-content: space-between;
    background: none;
    border: none;
    padding: 0.75rem 10px;
    border-bottom: 1px solid var(--color-border-light);
  }

  .dropdown-item {
    border-left: 2px solid var(--color-primary);
    background-color: transparent;
  }
}

/* Footer responsive styles */
@media (min-width: 768px) {
  .footer-content {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }

  .footer-main {
    flex: 1;
    text-align: left;
  }

  .footer-legal {
    flex: 2;
    padding-top: 0;
    border-top: none;
    border-left: 1px solid var(--color-border-light);
    padding-left: 2rem;
  }

  .footer-links {
    justify-content: flex-start;
  }
}
</style>